import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

interface Stats {
  total: number; alive: number; deleted: number; with_email: number;
  verification: { valid: number; invalid: number; risky: number };
  review: { pending: number; relevant: number; not_relevant: number };
}

export default function Dashboard() {
  const [s, setS] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    api.stats().then(setS).catch(e => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  if (err) return <div className="card p-5 text-rose-700">{err}</div>;
  if (!s) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Outreach</h1>
          <p className="mt-1 text-sm text-slate-500">Cold-outreach pipeline · curate → verify → campaign</p>
        </div>
        <div className="flex gap-2">
          <Link to="/outreach/import" className="btn-secondary">Import</Link>
          <Link to="/outreach/contacts" className="btn-primary">Open contacts</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <MetricCard label="Contacts (alive)" value={s.alive.toLocaleString()} sub={`${s.deleted.toLocaleString()} deleted · ${s.total.toLocaleString()} total`} />
        <MetricCard label="With email" value={s.with_email.toLocaleString()} sub={`${s.alive ? Math.round(100 * s.with_email / s.alive) : 0}% coverage`} />
        <MetricCard label="Pending review" value={s.review.pending.toLocaleString()} sub={`${s.review.relevant.toLocaleString()} marked relevant`} accent="from-amber-50 to-white" />
        <MetricCard label="Verified valid" value={s.verification.valid.toLocaleString()} sub={`${s.verification.risky.toLocaleString()} risky · ${s.verification.invalid.toLocaleString()} invalid`} accent="from-emerald-50 to-white" />
      </div>

      <section className="card p-5">
        <h2 className="section-title">Quick filters</h2>
        <p className="mt-1 text-xs text-slate-500">Jump into the contacts table with a pre-applied filter.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link to="/outreach/contacts?review_status=pending" className="chip bg-amber-50 text-amber-700 ring-1 ring-amber-200/60 hover:bg-amber-100">{s.review.pending} pending review</Link>
          <Link to="/outreach/contacts?review_status=relevant&verification_status=unchecked" className="chip bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 hover:bg-indigo-100">Relevant · need verify</Link>
          <Link to="/outreach/contacts?verification_status=valid" className="chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100">{s.verification.valid} verified valid</Link>
          <Link to="/outreach/contacts?has_email=0" className="chip bg-rose-50 text-rose-700 ring-1 ring-rose-200/60 hover:bg-rose-100">No email on file</Link>
          <Link to="/outreach/contacts?title_contains=hostess" className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200">Title contains "hostess"</Link>
          <Link to="/outreach/contacts?title_contains=student" className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200">Title contains "student"</Link>
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value, sub, accent = "from-slate-50 to-white" }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="card card-hover relative overflow-hidden p-5">
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accent} opacity-70`} />
      <div className="relative">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="mt-2 text-2xl font-semibold tracking-tightish text-slate-900">{value}</div>
        {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}
